/**
 * as-op-status
 *
 * Generic inline status indicator for async operations.
 *
 * States (controlled via the `state` attribute or the helper methods):
 *   (absent / "idle") — hidden, unless tooltip-default-text is set (shows ? icon)
 *   "pending"         — animated spinner
 *   "success"         — green checkmark; optional tooltip set by `message` attr
 *   "error"           — red cross;       optional tooltip set by `message` attr
 *   "info"            — ? icon with tooltip set by `message` attr; for non-error
 *                       informational states (e.g. "nothing to export yet")
 *
 * Attributes:
 *   state   — "idle" | "pending" | "success" | "error" | "info"
 *   message — tooltip text shown on hover in success/error/info states
 *
 * Programmatic helpers:
 *   el.setPending()
 *   el.setSuccess('Assembly ID: abc123')
 *   el.setError('Host rejected the package')
 *   el.setInfo('Nothing added to the exporter yet')
 *   el.reset()   // back to idle (hidden, or ? if tooltip-default-text is set)
 *
 * CSS custom properties:
 *   --op-status-size        icon/spinner diameter  (default 1.4rem)
 *   --op-status-color-ok    success colour         (default #40a02b)
 *   --op-status-color-err   error colour           (default #d20f39)
 *   --op-status-color-info  info ? colour          (default rgba(0,0,0,0.35))
 */
import '../overlays/as-tooltip-wrapper.ts';
import './as-spinner.ts';
import styleText from './as-op-status.css?raw';

export type OpStatusState = 'idle' | 'pending' | 'success' | 'error' | 'info';

const HELP_SVG = /*html*/`
  <svg class="icon help" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="10" cy="10" r="8.5" stroke="currentColor" stroke-width="1.5"/>
    <path d="M7.5 8C7.5 6.12 8.62 5 10 5s2.5 1.12 2.5 2.5C12.5 9 10 9.5 10 11"
          stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <circle cx="10" cy="13.5" r="0.75" fill="currentColor"/>
  </svg>`;

const CHECK_SVG = /*html*/`
  <svg class="icon success" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="10" cy="10" r="8.5" stroke="currentColor" stroke-width="1.5"/>
    <path d="M6.5 10.5l2.5 2.5 5-5" stroke="currentColor" stroke-width="2"
          stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;

const CROSS_SVG = /*html*/`
  <svg class="icon error" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="10" cy="10" r="8.5" stroke="currentColor" stroke-width="1.5"/>
    <path d="M7 7l6 6M13 7l-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
  </svg>`;

export class AsOpStatus extends HTMLElement {
  static readonly tag = 'as-op-status' as const;

  static get observedAttributes() { return ['state', 'message', 'tooltip-default-text']; }

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsOpStatus._sheet];
    this._render();
  }

  // ── Attribute accessors ────────────────────────────────────────────────────

  get state(): OpStatusState {
    return (this.getAttribute('state') as OpStatusState) ?? 'idle';
  }

  get message(): string {
    return this.getAttribute('message') ?? '';
  }

  // ── Programmatic API ───────────────────────────────────────────────────────

  setPending(): void {
    this.removeAttribute('message');
    this.setAttribute('state', 'pending');
  }

  setSuccess(msg = ''): void {
    if (msg) this.setAttribute('message', msg);
    else this.removeAttribute('message');
    this.setAttribute('state', 'success');
  }

  setError(msg = ''): void {
    if (msg) this.setAttribute('message', msg);
    else this.removeAttribute('message');
    this.setAttribute('state', 'error');
  }

  setInfo(msg = ''): void {
    if (msg) this.setAttribute('message', msg);
    else this.removeAttribute('message');
    this.setAttribute('state', 'info');
  }

  reset(): void {
    this.removeAttribute('message');
    this.removeAttribute('state');
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  attributeChangedCallback(): void {
    if (this.shadowRoot) this._render();
  }

  // ── Rendering ─────────────────────────────────────────────────────────────

  private _render(): void {
    const state = this.state;

    if (state === 'idle') {
      const tooltipText = this.getAttribute('tooltip-default-text');
      if (tooltipText) {
        this.shadowRoot!.innerHTML = /*html*/`
          <as-tooltip-wrapper show-on-hover placement="top" default-text="${this._esc(tooltipText)}" auto-flip>
            ${HELP_SVG}
          </as-tooltip-wrapper>`;
      } else {
        this.shadowRoot!.innerHTML = '';
      }
      return;
    }

    if (state === 'pending') {
      this.shadowRoot!.innerHTML = /*html*/`<as-spinner></as-spinner>`;
      return;
    }

    if (state === 'info') {
      const message = this.message;
      this.shadowRoot!.innerHTML = message
        ? /*html*/`
          <as-tooltip-wrapper show-on-hover placement="top" default-text="${this._esc(message)}" auto-flip>
            ${HELP_SVG}
          </as-tooltip-wrapper>`
        : HELP_SVG;
      return;
    }

    const iconHtml = state === 'success' ? CHECK_SVG : CROSS_SVG;
    const message = this.message;

    if (message) {
      this.shadowRoot!.innerHTML = /*html*/`
        <as-tooltip-wrapper show-on-hover placement="top" default-text="${this._esc(message)}" auto-flip>
          ${iconHtml}
        </as-tooltip-wrapper>`;
    } else {
      this.shadowRoot!.innerHTML = iconHtml;
    }
  }

  private _esc(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}

if (!customElements.get(AsOpStatus.tag)) {
  customElements.define(AsOpStatus.tag, AsOpStatus);
} else {
  console.warn('as-op-status already defined');
}
